<template>
  <div class="login-page">
    <div class="bg-layer">
      <span class="orb orb-1"></span>
      <span class="orb orb-2"></span>
      <span class="orb orb-3"></span>
      <span class="grid"></span>
    </div>

    <el-card class="login-card" shadow="never">
      <div class="card-header">
        <div class="brand">产品型号管理系统</div>
        <h2>欢迎登录</h2>
        <p class="subtitle">请使用账号密码与验证码进入系统</p>
      </div>
      
      <el-form 
        ref="loginFormRef"
        :model="form" 
        :rules="rules"
        class="login-form" 
        @submit.prevent="onSubmit"
      >
        <el-form-item prop="username">
          <el-input
            v-model="form.username"
            placeholder="请输入帐号"
            size="large"
            prefix-icon="User"
          />
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input
            v-model="form.password"
            type="password"
            placeholder="请输入密码"
            size="large"
            prefix-icon="Lock"
            show-password
          />
        </el-form-item>
        
        <el-form-item prop="captcha_code">
          <div class="captcha-container">
            <div class="captcha-input">
              <el-input
                v-model="form.captcha_code"
                placeholder="请输入验证码"
                size="large"
                prefix-icon="Key"
              />
            </div>
            <div class="captcha-image" @click="loadCaptcha">
              <el-image
                v-if="captchaSrc"
                :src="captchaSrc"
                alt="验证码"
                fit="contain"
                class="captcha-img"
              >
                <template #error>
                  <div class="captcha-error">加载失败</div>
                </template>
              </el-image>
              <div v-else class="captcha-placeholder">
                <!-- eslint-disable-next-line no-unused-vars -->
                <el-button type="primary" icon="Refresh" circle @click.stop="loadCaptcha" />
              </div>
            </div>
          </div>
        </el-form-item>
        
        <el-form-item>
          <el-button 
            type="primary" 
            size="large" 
            native-type="submit"
            :loading="loading"
            class="login-btn"
          >
            登录
          </el-button>
        </el-form-item>
      </el-form>
      
      <el-alert
        v-if="error"
        :title="error"
        type="error"
        show-icon
        :closable="false"
        class="error-alert"
      />
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { getCaptcha, login } from '../services/auth'
import router from '../router'
import { extractErrorMessage } from '../utils/errorHandler'

export default {
  name: 'AuthLogin',
  setup() {
    const form = reactive({ username: '', password: '', captcha_code: '' })
    const captchaSrc = ref(null)
    const error = ref(null)
    const loading = ref(false)
    const loginFormRef = ref(null)
    let _prevCaptchaUrl = null

    // 表单验证规则
    const rules = {
      username: [
        { required: true, message: '请输入帐号', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' }
      ],
      captcha_code: [
        { required: true, message: '请输入验证码', trigger: 'blur' }
      ]
    }

    async function loadCaptcha() {
      error.value = null
      try {
        const data = await getCaptcha()
        // Backend returns JSON: {"captcha_image": "data:image/png;base64,IVBORw0KGgo..."}
        // The captcha_image field already contains the full data URI
        if (data && data.captcha_image) {
          // Use the data URI directly (already includes data:image/png;base64,)
          captchaSrc.value = data.captcha_image
        } else if (data instanceof Blob) {
          // Fallback for blob response (if backend changes)
          if (_prevCaptchaUrl) {
            URL.revokeObjectURL(_prevCaptchaUrl)
            _prevCaptchaUrl = null
          }
          const url = URL.createObjectURL(data)
          _prevCaptchaUrl = url
          captchaSrc.value = url
        } else if (typeof data === 'string') {
          // Fallback for plain base64 string (if backend changes)
          captchaSrc.value = `data:image/png;base64,${data}`
        } else {
          captchaSrc.value = null
          ElMessage.error('验证码格式错误')
        }
      } catch (e) {
        error.value = '无法获取验证码，请检查后端是否运行并允许跨域。'
        ElMessage.error(error.value)
      }
    }

    // cleanup when component unmounts
    onUnmounted(() => {
      if (_prevCaptchaUrl) {
        URL.revokeObjectURL(_prevCaptchaUrl)
        _prevCaptchaUrl = null
      }
    })

    async function onSubmit() {
      if (!loginFormRef.value) return
      
      await loginFormRef.value.validate(async (valid) => {
        if (!valid) return
        
        error.value = null
        loading.value = true
        
        try {
          const res = await login(form)
          if (res && res.access_token) {
            localStorage.setItem('access_token', res.access_token)
            ElMessage.success('登录成功')
            router.push('/')  // 登录成功后跳转到 Index，然后默认显示 Dashboard
          } else {
            error.value = '登录失败，返回数据不完整。'
            ElMessage.error(error.value)
            loadCaptcha()
          }
      } catch (e) {
        error.value = extractErrorMessage(e)
        ElMessage.error(error.value)
        loadCaptcha()
      } finally {
          loading.value = false
        }
      })
    }

    onMounted(() => {
      loadCaptcha()
    })

    return { 
      form, 
      captchaSrc, 
      error, 
      loading,
      loginFormRef,
      rules,
      loadCaptcha, 
      onSubmit 
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700&display=swap');

.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(1200px 500px at 10% 10%, rgba(255, 255, 255, 0.4), transparent),
              radial-gradient(1000px 600px at 90% 20%, rgba(99, 102, 241, 0.2), transparent),
              linear-gradient(135deg, #0f172a 0%, #1f2937 50%, #0b1324 100%);
  padding: 32px 20px;
  position: relative;
  overflow: hidden;
  font-family: 'Manrope', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.bg-layer {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(10px);
  opacity: 0.7;
  animation: float 16s ease-in-out infinite;
}

.orb-1 {
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.8), rgba(56, 189, 248, 0));
  top: -60px;
  left: -40px;
}

.orb-2 {
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgba(236, 72, 153, 0.8), rgba(236, 72, 153, 0));
  bottom: -80px;
  right: -20px;
  animation-delay: -6s;
}

.orb-3 {
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.7), rgba(59, 130, 246, 0));
  top: 45%;
  right: 15%;
  animation-delay: -10s;
}

.grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 24px 24px;
  mask-image: radial-gradient(circle at 50% 50%, rgba(0, 0, 0, 0.9), transparent 70%);
}

.login-card {
  width: 420px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(16px);
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.35);
  padding: 24px 20px 8px;
  z-index: 1;
}

.card-header {
  text-align: left;
  margin-bottom: 18px;
  display: grid;
  gap: 6px;
}

.brand {
  font-size: 13px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #6366f1;
  font-weight: 700;
}

.card-header h2 {
  margin: 0;
  color: #0f172a;
  font-weight: 700;
  font-size: 24px;
}

.subtitle {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.login-form {
  margin-top: 12px;
}

.captcha-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.captcha-input {
  flex: 1;
}

.captcha-image {
  width: 120px;
  height: 40px;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
}

.captcha-img {
  width: 100%;
  height: 100%;
}

.captcha-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

.captcha-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1 0%, #38bdf8 100%);
  border: none;
  box-shadow: 0 10px 20px rgba(99, 102, 241, 0.25);
}

.error-alert {
  margin-top: 20px;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) translateX(0);
  }
  50% {
    transform: translateY(-20px) translateX(10px);
  }
}

/* 响应式设计 */
@media (max-width: 480px) {
  .login-card {
    width: 100%;
    margin: 0 10px;
  }
  
  .captcha-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .captcha-image {
    width: 100%;
    height: 44px;
  }
}
</style>
